import { useCopy } from '@hooks/copy';
import { Bundle } from '@local-types/bundle';
import { Copies } from '../constants';
import { Avatar, Stack, Typography } from '@mui/material';
import { AvatarImageStyled, MoreLinkStyled } from '../styles';

export { fields, Copies } from '../constants';

export const inventoryTypes = {
  bundles: 'bundles',
  padmanPremiumReach: 'padman-premium-reach',
  premiumReach: 'premium-reach',
  performanceOptimized: 'performanceOptimized',
  premiumDisplay: 'premiumDisplay',
  private: 'private',
  reach: 'reach',
} as const;

export const getInventoryOptions = ({
  customInventoryItems = [],
  onAdvancedCustomInventory,
  isCustomInventoryExpanded,
  setIsCustomInventoryExpanded,
  isDisplay = false,
  domain,
  bundles,
}: {
  customInventoryItems?: Bundle[];
  onAdvancedCustomInventory?: () => void;
  isCustomInventoryExpanded?: boolean;
  setIsCustomInventoryExpanded?: () => void;
  isDisplay?: boolean;
  domain: Record<string, boolean>;
  bundles: Bundle[];
}) => {
  const Copy = useCopy(Copies);

  if (isDisplay) {
    return [
      {
        value: inventoryTypes.premiumDisplay,
        label: 'Premium Display',
        locked: false,
        description:
          'Brand-safe and cost-effective website and app display inventory, \
        to extend your reach',
      },
    ];
  }

  return [
    ...(!domain.peacock
      ? [
          {
            value: inventoryTypes.performanceOptimized,
            label: 'Performance Optimized',
            description:
              'Inventory is organized by bundle, content, network, and app \
              type for narrow content or publisher targeting. Best for \
              performance advertisers looking to maximize exposure but drive \
              down CPA/CAC, while maintaining brand safety and prioritizing \
              target audience above content.',
            infoText: (
              <Typography component="span" variant="body2">
                <a
                  href="https://help.tvscientific.app/en/articles/7211806-supply-101"
                  target="_blank"
                  title=""
                  rel="noreferrer"
                  style={{ textDecoration: 'none' }}
                >
                  Learn more about Performance Optimized inventory
                </a>
              </Typography>
            ),
            locked: !bundles?.some(({ recommended }) => recommended),
            unlockedContent: <Stack />,
            recommended: false,
            lockedTooltipInfo:
              'We need more historical performance data to make inventory recommendations for this Ad Group.',
            lockedInfo:
              'Recommendations not available for this Campaign, please select Maximum Reach or Custom Inventory.',
            recommendedInfo:
              'Recommended based on your campaign objective and bid strategy',
          },
        ]
      : []),
    {
      value: domain.peacock
        ? inventoryTypes.padmanPremiumReach
        : inventoryTypes.premiumReach,
      label: 'Maximum Reach',
      locked: false,
      description:
        'Brand safe, high quality publishers, with lower costs due to a broad \
        range of living room quality content. Best for broad branding \
        advertisers that are targeting a wider audience.',
      infoText: 'Learn more about Maximum Reach inventory',
    },
    {
      value: inventoryTypes.bundles,
      label: 'Custom Inventory',
      locked: false,
      description: (
        <Stack
          onClick={onAdvancedCustomInventory}
          style={{ marginBottom: 2.5 }}
          data-testid="custom-inventory-option"
        >
          <Typography component="span" variant="body2">
            {Copy.customInventoryDescription}
          </Typography>
        </Stack>
      ),
      unlockedContent: (
        <Stack
          onClick={onAdvancedCustomInventory}
          spacing={1}
          sx={{ marginBottom: 2.5 }}
        >
          <Stack direction="row" spacing={1}>
            {customInventoryItems
              .slice(
                0,
                isCustomInventoryExpanded ? customInventoryItems.length : 2
              )
              .map(i => (
                <Stack
                  alignItems="center"
                  direction="row"
                  spacing={1}
                  key={i.id}
                >
                  <Avatar
                    sx={{
                      bgcolor: i.icon?.url ? 'transparent' : 'grey.8',
                    }}
                    variant="square"
                  >
                    {i.icon ? (
                      <AvatarImageStyled src={i.icon?.url} />
                    ) : (
                      <span />
                    )}
                  </Avatar>
                  <Typography variant="body1" fontWeight={700}>
                    {i.display_name}
                  </Typography>
                </Stack>
              ))}
          </Stack>
          {!isCustomInventoryExpanded && customInventoryItems.length > 2 ? (
            <MoreLinkStyled
              underline="none"
              sx={{
                cursor: 'pointer',
              }}
              onClick={e => {
                e.preventDefault();
                setIsCustomInventoryExpanded?.();
              }}
            >
              + {customInventoryItems.length - 2} more...
            </MoreLinkStyled>
          ) : null}
        </Stack>
      ),
    },
  ];
};
